<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Phone, Clock, User, FileText, FolderTree, BellRing, Timer, Check, Link2 } from 'lucide-vue-next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { callbackRemindersApi } from '@/api/callback-reminders'

const router = useRouter()

interface CallbackAlert {
  id: number
  log_id: number
  lead_id: number
  lead_name: string
  scheduled_date: string
  scheduled_time: string | null
  phone?: string
  campaign?: string
  time?: string
  reason?: string
  operator?: string
  codificacion?: string
}

const visible = ref(false)
const alert = ref<CallbackAlert>({
  id: 0,
  log_id: 0,
  lead_id: 0,
  lead_name: '',
  scheduled_date: '',
  scheduled_time: null,
})

let postponeTimer: ReturnType<typeof setTimeout> | null = null

const show = (data: CallbackAlert) => {
  alert.value = data
  visible.value = true
}

const dismiss = () => {
  visible.value = false
}

const handleAccept = async () => {
  try {
    await callbackRemindersApi.reportAction(alert.value.log_id, 'accepted')
  } catch {}
  dismiss()
}

const handleGoToLead = async () => {
  try {
    await callbackRemindersApi.reportAction(alert.value.log_id, 'went_to_lead')
  } catch {}
  dismiss()
  router.push(`/leads/${alert.value.lead_id}`)
}

const handlePostpone = async () => {
  try {
    await callbackRemindersApi.reportAction(alert.value.log_id, 'postponed_5m')
  } catch {}
  dismiss()
  if (postponeTimer) clearTimeout(postponeTimer)
  postponeTimer = setTimeout(() => {
    visible.value = true
  }, 5 * 60 * 1000)
}

const formatDateTime = (date: string, time: string | null): string => {
  if (!date) return ''
  const d = new Date(date + 'T00:00:00')
  const dateStr = d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
  if (time) {
    return `${dateStr} ${time.slice(0, 5)}`
  }
  return dateStr
}

defineExpose({ show })
</script>

<template>
  <Dialog :open="visible" @update:open="(v) => !v && dismiss()">
    <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-md">
      <DialogHeader>
        <div class="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400">
          <BellRing class="size-6" />
        </div>
        <DialogTitle class="text-center">Recordatorio de Callback</DialogTitle>
        <DialogDescription class="sr-only">Tienes un callback pendiente</DialogDescription>
      </DialogHeader>

      <div class="space-y-2">
        <div class="text-center text-lg font-semibold">{{ alert.lead_name }}</div>

        <div v-if="alert.phone" class="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <Phone class="size-4 text-muted-foreground" />
          <span>{{ alert.phone }}</span>
        </div>
        <div v-if="alert.campaign" class="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <FolderTree class="size-4 text-muted-foreground" />
          <span>{{ alert.campaign }}</span>
        </div>
        <div v-if="alert.time" class="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <Clock class="size-4" />
          <span>{{ formatDateTime(alert.scheduled_date, alert.scheduled_time) }}</span>
        </div>
        <div v-if="alert.reason" class="flex items-center gap-2 rounded-md border px-3 py-2 text-sm italic text-muted-foreground">
          <FileText class="size-4" />
          <span>{{ alert.reason }}</span>
        </div>
        <div v-if="alert.operator" class="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <User class="size-4 text-muted-foreground" />
          <span>{{ alert.operator }}</span>
        </div>
        <div v-if="alert.codificacion" class="flex justify-center pt-1">
          <Badge variant="secondary">{{ alert.codificacion }}</Badge>
        </div>
      </div>

      <DialogFooter class="flex-wrap justify-center gap-2 sm:justify-center">
        <Button variant="outline" @click="handlePostpone">
          <Timer class="size-4" />
          Posponer 5 min
        </Button>
        <Button variant="default" @click="handleAccept">
          <Check class="size-4" />
          Aceptar
        </Button>
        <Button variant="secondary" @click="handleGoToLead">
          <Link2 class="size-4" />
          Ir al Lead
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>